import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, Effect } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { Address, DistributionOverviewItem, SectorPolygon, Street, WeekOfYear } from '../../domain/models';
import { DistributionDetailService } from '../services/distribution-detail.service';
import { DistributionListService } from '../services/distribution-list.service';
import { DistributionViewModel } from '../view-models/distribution.view-model';
import { DistributionListItemViewModel } from '../view-models/distribution-list-item.view-model';
import * as AllActions from './distribution.actions';
import * as _ from 'lodash';
import { TranslateService } from 'ng2-translate';
import { TrackingData } from '../../domain/models/tracking/tracking_data';
import { DeviceViewModel } from '../view-models/device.view-model';
import Constants from '../../shared/constants';

@Injectable()
export class DistributionEffects {

    @Effect()
    loadWeeks$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadWeeks>(AllActions.LOAD_WEEKS)
        .switchMap(() =>
            this.distributionService.getWeekOfYearList()
                .mergeMap((result: WeekOfYear[]) => {
                    const weekParam: string = this.route.snapshot.queryParams.week;
                    return [
                        new AllActions.LoadWeeksCompleted(result),
                        new AllActions.SetActiveWeek(weekParam ? parseInt(weekParam, 10) : result[0].weekNumber)
                    ];
                })
        );

    @Effect({ dispatch: false })
    setActiveWeek$: Observable<number> = this.actions$
        .ofType<AllActions.SetActiveWeek>(AllActions.SET_ACTIVE_WEEK)
        .map((action: AllActions.SetActiveWeek) => action.payload)
        .do((week: number) => {
            this.router.navigate([], { relativeTo: this.route, queryParams: { week: week } });
        });

    @Effect()
    loadDistributions$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadDistributions>(AllActions.LOAD_DISTRIBUTIONS)
        .mergeMap((action: AllActions.LoadDistributions) =>
            this.distributionService.getDistributionOverviewByWeekNumber(action.payload)
                .map((result: DistributionOverviewItem[]) => new AllActions.LoadDistributionsSuccess(this.mapDistributionOverviewItemsToDistributionListItemViewModels(result)))
                .catch(() => of(new AllActions.LoadDistributionsFailed()))
        );

    @Effect()
    loadSectors$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadSectors>(AllActions.LOAD_SECTORS)
        .mergeMap((action: AllActions.LoadSectors) =>
            this.distributionService.getSectorsByIds(_.uniq(action.payload))
                .map((result: SectorPolygon[]) => new AllActions.LoadSectorsSuccess(result))
                .catch(() => of(new AllActions.LoadSectorsFailed()))
        );

    @Effect()
    loadDetail$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadDistributionDetail>(AllActions.LOAD_DISTRIBUTION_DETAIL)
        .mergeMap((action: AllActions.LoadDistributionDetail) =>
            this.distributionDetailService.getDistributionDetailById(action.payload)
                .map((result: DistributionViewModel) => {
                    result.devices.forEach((device: DeviceViewModel, index: number) => {
                        device.deviceColor = Constants.DeviceColors[index];
                    });
                    return new AllActions.LoadDistributionDetailSuccess(result);
                })
                .catch(() => {
                    return of(new AllActions.LoadDistributionDetailFailed());
                })
        );

    @Effect()
    loadTrackingData$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadDistributionDetailSuccess>(AllActions.LOAD_DISTRIBUTION_DETAIL_SUCCESS)
        .mergeMap((action: AllActions.LoadDistributionDetailSuccess) => {
            return this.distributionDetailService.getTrackingData(action.payload.id)
                .map((result: TrackingData) => {
                    return new AllActions.LoadTrackingDataSuccess(result);
                });
        });

    @Effect()
    setSectorId$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadDistributionDetailSuccess>(AllActions.LOAD_DISTRIBUTION_DETAIL_SUCCESS)
        .switchMap((action: AllActions.LoadDistributionDetailSuccess) => [
            new AllActions.LoadSectors([action.payload.sectorId]),
            new AllActions.LoadAddresses(action.payload.addresses, action.payload.sectorId),
            new AllActions.LoadStreets(action.payload.streets, action.payload.sectorId)
        ]);

    @Effect()
    loadStreets$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadStreets>(AllActions.LOAD_STREETS)
        .mergeMap((action: AllActions.LoadStreets) =>
            this.distributionDetailService.getStreetsBySectorId(action.sectorId)
                .map((result: Street[]) => new AllActions.LoadStreetsSuccess(action.distributionStreets, result))
                .catch(() => of(new AllActions.LoadStreetsFailed()))
        );
    @Effect()
    loadAddresses$: Observable<Action> = this.actions$
        .ofType<AllActions.LoadAddresses>(AllActions.LOAD_ADDRESSES)
        .mergeMap((action: AllActions.LoadAddresses) =>
            this.distributionDetailService.getAddressBySectorId(action.sectorId)
                .map((result: Address[]) => new AllActions.LoadAddressesSuccess(action.distributionAddresses, result))
                .catch(() => of(new AllActions.LoadAddressesFailed()))
        );

    @Effect()
    startDistribution$: Observable<Action> = this.actions$
        .ofType<AllActions.StartDistribution>(AllActions.START_DISTRIBUTION)
        .mergeMap((action: AllActions.StartDistribution) =>
            this.distributionDetailService.startDistribution(action.payload)
                .map(() => new AllActions.StartDistributionSuccess(action.payload))
                .catch(() => of(new AllActions.StartDistributionFailed()))
        );

    @Effect()
    stopDistribution$: Observable<Action> = this.actions$
        .ofType<AllActions.StopDistribution>(AllActions.STOP_DISTRIBUTION)
        .mergeMap((action: AllActions.StopDistribution) =>
            this.distributionDetailService.stopDistribution(action.payload)
                .map(() => new AllActions.StopDistributionSuccess(action.payload.distributionId))
                .catch(() => of(new AllActions.StopDistributionFailed()))
        );

    @Effect()
    reloadDistributionDetailFromStart$: Observable<Action> = this.actions$
        .ofType<AllActions.StartDistributionSuccess>(AllActions.START_DISTRIBUTION_SUCCESS)
        .mergeMap((action: AllActions.StartDistributionSuccess) =>
            of(new AllActions.LoadDistributionDetail(action.payload))
        );

    @Effect()
    reloadDistributionDetailFromStop$: Observable<Action> = this.actions$
        .ofType<AllActions.StopDistributionSuccess>(AllActions.STOP_DISTRIBUTION_SUCCESS)
        .mergeMap((action: AllActions.StopDistributionSuccess) =>
            of(new AllActions.LoadDistributionDetail(action.payload))
        );

    private mapDistributionOverviewItemsToDistributionListItemViewModels(distributions: DistributionOverviewItem[]): DistributionListItemViewModel[] {
        let distributionViewModels: DistributionListItemViewModel[] = [];
        let translate: TranslateService = this.translateService;
        distributions.forEach(function (distribution: DistributionOverviewItem): void {
            let distributionItemViewModel: DistributionListItemViewModel = new DistributionListItemViewModel(distribution, null);
            if (distribution.distributionProgressEarly !== 0 && distribution.distributionProgressLate !== 0) {
                distributionItemViewModel.distributionTiming = translate.instant('DISTRIBUTION_TYPE_BOTH');
            } else if (distribution.distributionProgressEarly !== 0) {
                distributionItemViewModel.distributionTiming = translate.instant('DISTRIBUTION_TYPE_EARLY');
            } else if (distribution.distributionProgressLate !== 0) {
                distributionItemViewModel.distributionTiming = translate.instant('DISTRIBUTION_TYPE_LATE');
            } else {
                distributionItemViewModel.distributionTiming = translate.instant('DISTRIBUTION_TYPE_ON_TIME_OVERVIEW');
            }

            distributionViewModels.push(distributionItemViewModel);
        });
        return distributionViewModels;
    }


    constructor(
        private actions$: Actions,
        private distributionService: DistributionListService,
        private distributionDetailService: DistributionDetailService,
        private translateService: TranslateService,
        private route: ActivatedRoute,
        private router: Router
    ) { }
}



// WEBPACK FOOTER //
// ./src/app/modules/distribution/store/distribution.effects.ts